import { cn } from "@/shadcn/lib/utils";

type ConsentRevokedKind = "images" | "analysis";

type ConsentRevokedPlaceholderProps = {
  /**
   * What kind of content was gated. Controls the default message.
   * - "images": raw imagery gated by biometric consent
   * - "analysis": AI-generated consumer-health analysis gated by consumer-health consent
   */
  readonly kind: ConsentRevokedKind;
  /**
   * Override the default message for the given kind.
   */
  readonly message?: string;
  /**
   * Additional classes applied to the outer wrapper. Use to control the
   * aspect ratio / size of the surrounding container (e.g. "aspect-square",
   * "aspect-3/4", "aspect-4/5", or "absolute inset-0").
   */
  readonly className?: string;
  /**
   * When true, renders the placeholder as an absolutely-positioned overlay
   * rather than a sized block. Use inside a `relative` container.
   */
  readonly overlay?: boolean;
};

const DEFAULT_MESSAGES: Record<ConsentRevokedKind, string> = {
  images: "This user has revoked consent to view their images",
  analysis: "This user has revoked consent to view their AI-generated consumer-health analysis",
};

export function ConsentRevokedPlaceholder({
  kind,
  message,
  className,
  overlay = false,
}: ConsentRevokedPlaceholderProps): React.ReactElement {
  const resolvedMessage = message ?? DEFAULT_MESSAGES[kind];

  if (overlay) {
    return (
      <div
        className={cn("absolute inset-0 z-10 flex items-center justify-center p-4", className)}
        data-testid="consent-revoked-placeholder"
        data-consent-kind={kind}
      >
        <ConsentRevokedBadge message={resolvedMessage} />
      </div>
    );
  }

  return (
    <div
      className={cn("flex w-full items-center justify-center bg-surface-muted p-4", className)}
      data-testid="consent-revoked-placeholder"
      data-consent-kind={kind}
    >
      <ConsentRevokedBadge message={resolvedMessage} />
    </div>
  );
}

function ConsentRevokedBadge({ message }: { readonly message: string }): React.ReactElement {
  return (
    <span className="max-w-xs rounded-md bg-ageless-amber px-4 py-3 text-center type-body-sm font-semibold text-foreground">
      {message}
    </span>
  );
}
